import { BasketItem as BasketItemType, ItemGroupTotal } from '../../../types/basket';
import { LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import {
  getGaEcommerceObject,
  pushErrorTrackingEventFromErrorObject,
  pushTrackingEvent
} from '../../../utils/tracking';
import Link from '../../atoms/Link';
import { showDefaultError } from '../Notifications';
import DeliveryForecast from './DeliveryForecast';
import ProductGroup from './Group';
import ItemHeading from './ItemHeading';
import ItemTotals from './ItemTotals';
import MerchantDetails from './MerchantDetails';
import ProductDetails from './ProductDetails';
import RemoveItemButton from './RemoveItemButton';
import SoldOut from './SoldOut';
import './style.scss';

type BasketItemProps = {
  item: BasketItemType & { subItems?: BasketItemType[]; groupTotal?: ItemGroupTotal };
  isSoldOut: boolean;
  shippingMerchantName?: string;
};

const BasketItem = ({ item, isSoldOut }: BasketItemProps) => {
  const { checkoutState, removeBasketItem, setBasketEmpty, isLoading, setLoading } = useCheckoutState();
  const shouldShowDeliveryForecast = item.deliveryForecast?.deliverable ?? item.deliveryForecast?.subsequentDelivery;
  const { hasDisabledLinks, hideItemRemovalButton, showMerchantInformation } = config.basket;
  const merchantInfo = checkoutState.merchants?.find(merchant => merchant.id === item.merchant.id)?.legalNotice;
  const shouldShowMerchantInfo = merchantInfo && showMerchantInformation;

  const locationSearch = new URLSearchParams(window.location.search);
  const isPaypalReviewPage = !!locationSearch.get('isReviewPage');
  const isAmazonPayReviewPage = !!locationSearch.get('isAmazonPayReviewPage');

  const onRemoveItem = async (id: string) => {
    setLoading(LOADING_TYPES.itemRemove(id));
    try {
      await removeBasketItem({ id }, { prevQuantity: item.quantity, removedBasketItem: item });
    } catch (e: any) {
      if (e.statusCode === 204) {
        pushTrackingEvent({
          event: 'remove_from_cart',
          action: `${window.location.hash}|${id}`,
          price_bi: null,
          ecommerce: getGaEcommerceObject(checkoutState, id, 1, item, true)
        });
        setBasketEmpty(true);
        return;
      }
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'remove_basket_item');
    }
    setLoading(LOADING_TYPES.off);
  };

  const imageBg = config.styles.imageBackgroundColor.replace('#', '');

  const imageParameters =
    config.basket.imageParameters ?
      config.basket.imageParameters
    : `?bg=${imageBg}&height=330&progressive=1&quality=90&width=260`;

  const imageStyle = {
    backgroundImage: `url('${item.imageUrl}${imageParameters}')`,
    opacity: isSoldOut ? '0.25' : '1'
  };

  const href =
    config.features.appDeepLinkRewrites.enabled && config.origin === 'app' ?
      `app://pdp/${item.productId}`
    : item.pdpUrl;

  return (
    <div className="basket-item-container" data-test-id={`basket-item-${item.id}`}>
      <div className="basket-item">
        <div className="basket-item__left-col">
          <Link
            className="basket-item__left-col__image"
            href={href}
            style={imageStyle}
            isDisabled={hasDisabledLinks}
            data-test-id={`basket-item-image-${item.id}`}
          />
        </div>
        {isSoldOut ?
          <SoldOut />
        : <div className="basket-item__right-col">
            <div className="basket-item__header">
              <ItemHeading name={item.name} brand={item.brand.name} href={href} />
              {!hideItemRemovalButton && !isPaypalReviewPage && !checkoutState.isExpressCheckout && (
                <RemoveItemButton itemId={item.id} onRemoveItem={onRemoveItem} isLoading={isLoading} />
              )}
            </div>
            <ProductDetails item={item} href={href} />
            {shouldShowMerchantInfo && <MerchantDetails details={merchantInfo} />}
            {shouldShowDeliveryForecast && <DeliveryForecast deliveryForecast={item.deliveryForecast!} />}
          </div>
        }
      </div>
      {!isSoldOut && <ItemTotals item={item} isLoading={isLoading} />}
      {item.subItems && (
        <ProductGroup
          subItems={item.subItems}
          isLoading={isLoading}
          onRemoveItem={onRemoveItem}
          total={item.groupTotal!}
        />
      )}
    </div>
  );
};

export default BasketItem;
